/**
 * Loading, empty and error states.
 *
 * Every interactive surface in this app has all four states, and the error one says what
 * happened and what to do next. "Something went wrong" is not an option — a salesperson who
 * cannot act on an error phones the owner, and the owner is not at the counter.
 */

import type { ReactNode } from 'react';

import { cn } from './cn.js';
import { Button } from './Button.js';

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        'inline-block h-5 w-5 animate-spin rounded-full border-2 border-line border-t-action',
        className,
      )}
    />
  );
}

/** Skeleton rows. Shaped like the content they stand in for, so the layout does not jump. */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-3 rounded-card bg-surface-raised p-4">
          <div className="h-10 w-10 shrink-0 animate-pulse rounded-md bg-skeleton" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-3/5 animate-pulse rounded bg-skeleton" />
            <div className="h-3 w-2/5 animate-pulse rounded bg-skeleton" />
          </div>
        </div>
      ))}
    </div>
  );
}

export type EmptyStateProps = {
  title: string;
  /** What to do about it. An empty state that only says "nothing here" wastes the screen. */
  body?: string;
  icon?: ReactNode;
  action?: ReactNode;
};

export function EmptyState({ title, body, icon, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-[3.5rem] text-center">
      {icon !== undefined && <div className="text-content-muted">{icon}</div>}
      <p className="text-lg font-semibold text-content-primary">{title}</p>
      {body !== undefined && <p className="max-w-[34ch] text-sm text-content-muted">{body}</p>}
      {action}
    </div>
  );
}

export type ErrorStateProps = {
  /** What happened, in the user's terms. */
  title: string;
  /** What to do next. Always present. */
  body: string;
  onRetry?: () => void;
  retryLabel?: string;
};

export function ErrorState({ title, body, onRetry, retryLabel = 'Try again' }: ErrorStateProps) {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 rounded-card border border-danger-border bg-danger-subtle px-6 py-10 text-center"
    >
      <p className="text-lg font-semibold text-danger-subtle-text">{title}</p>
      <p className="max-w-[38ch] text-sm text-danger-subtle-text/90">{body}</p>
      {onRetry !== undefined && (
        <Button variant="quiet" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

/** The inline version, for a banner above a form rather than a whole screen. */
export function InlineError({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className="rounded-md border border-danger-border bg-danger-subtle px-3 py-2 text-sm font-medium text-danger-subtle-text"
    >
      {message}
    </p>
  );
}
